import React, { useRef, useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { compassColors } from "@utils/styling";
import Button from "src/design-system/components/Button/Button";
import { Text } from "@components/core/Text";
import {
  fontLargeHeadline,
  fontP3Regular,
} from "src/design-system/styles/fonts";
import { useGlobalEventListener } from "src/hooks/dom";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
`;

const ModalContainer = styled.div<{ translateY: number; isDragging: boolean }>`
  width: 100%;
  max-height: 80vh;
  background-color: ${compassColors.white};
  border-radius: 20px 20px 0 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  touch-action: none;
  transform: translateY(${(props) => props.translateY}px);
  transition: ${(props) =>
    props.isDragging ? "none" : "transform 0.3s ease-out"};
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const DragHandle = styled.div`
  width: 48px;
  height: 4px;
  background-color: ${compassColors.grey1};
  border-radius: 2px;
  margin: 8px auto;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const ButtonSection = styled.div`
  padding: 32px 16px 32px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex-shrink: 0;
  margin-top: auto;
`;

const StyledTitle = styled.h2`
  ${fontLargeHeadline};
  letter-spacing: -0.001em;
  line-height: 34px;
  font-weight: 400;
  text-align: left;
  margin: 32px 0 0;
  padding: 0 24px;
`;

const StyledSubtitle = styled.h2`
  ${fontP3Regular};
  color: ${compassColors.grey4};
  font-weight: 400;
  text-align: left;
  padding: 0 24px;
`;

interface ConfirmationModalProps {
  onClose: () => void;
  onPrimaryAction: () => void;
  dataCy?: string;
}

function ConfirmationModal({
  onClose,
  onPrimaryAction,
  dataCy,
}: ConfirmationModalProps) {
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const currentTranslateY = useRef(0);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleEventStart = (clientY: number) => {
    if (isDragging) return;
    startY.current = clientY;
    setIsDragging(true);
    setTranslateY(0);
  };

  const handleEventMove = (clientY: number) => {
    if (!isDragging) return;

    const deltaY = Math.max(0, clientY - startY.current); // Only allow downward drag
    currentTranslateY.current = deltaY;
    setTranslateY(deltaY);
  };

  const handleEventEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const threshold = 50; // 50px drag to close
    if (currentTranslateY.current > threshold) {
      onClose();
    } else {
      setTranslateY(0); // Snap back
    }
  };

  useGlobalEventListener(
    "mousemove",
    (e: MouseEvent) => handleEventMove(e.clientY),
    { passive: true }
  );
  useGlobalEventListener("mouseup", handleEventEnd, { passive: true });

  return (
    <ModalOverlay onClick={handleOverlayClick} data-cy={dataCy}>
      <ModalContainer
        translateY={translateY}
        isDragging={isDragging}
        data-cy={`${dataCy}-container`}
        onTouchStart={(e: React.TouchEvent) =>
          handleEventStart(e.touches[0].clientY)
        }
        onTouchMove={(e: React.TouchEvent) =>
          handleEventMove(e.touches[0].clientY)
        }
        onTouchEnd={handleEventEnd}
        onMouseDown={(e: React.MouseEvent) => handleEventStart(e.clientY)}
      >
        <DragHandle data-cy={`${dataCy}-drag-handle`} />
        <StyledTitle data-cy={`${dataCy}-title`}>
          <Text i18nKey="globalExitButton:confirmationModal:title" />
        </StyledTitle>
        <StyledSubtitle data-cy={`${dataCy}-title`}>
          <Text i18nKey="globalExitButton:confirmationModal:subtitle" />
        </StyledSubtitle>
        <ButtonSection>
          <Button
            designStyle="medPrimaryCta"
            onClick={onPrimaryAction}
            data-cy={`${dataCy}-primary-button`}
            css={css`
              &:focus {
                outline: 0px;
                outline-offset: 0px;
              }
            `}
          >
            <Text i18nKey="globalExitButton:confirmationModal:primaryButtonText" />
          </Button>
          <Button
            designStyle="secondaryCta"
            onClick={onClose}
            data-cy={`${dataCy}-secondary-button`}
            css={css`
              color: ${compassColors.black};
              border: solid 1px ${compassColors.black};
              border-radius: 999px;
              &:hover:enabled {
                color: ${compassColors.black};
                background-color: ${compassColors.white};
              }
              &:focus {
                border: solid 1px ${compassColors.black};
                outline: 0px;
                outline-offset: 0px;
              }
            `}
          >
            <Text i18nKey="globalExitButton:confirmationModal:secondaryButtonText" />
          </Button>
        </ButtonSection>
      </ModalContainer>
    </ModalOverlay>
  );
}

export default ConfirmationModal;
